import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Grid, makeStyles } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import clientPageStyle from "features/client/styles";
import { emptyArray, emptyString } from "constants/defaultValues";
import { get, set } from "@totalsoft/change-tracking-react";
import { onTextBoxChange } from "utils/propertyChangeAdapters";
import { Autocomplete, CustomTextField } from "@totalsoft/rocket-webapp-components-deprecated";
import { useClientQueryWithErrorHandling } from "hooks/errorHandling";
import { CITIES_QUERY, USER_DATA } from "features/common/queries";
import { cityTypes, countryCodeConstants } from "features/common/constants";
import { getErrors, isValid } from "@totalsoft/pure-validations";
import { getTenant } from "providers/TenantProvider";
import { useApolloClient } from "@apollo/client";

const useStyles = makeStyles(clientPageStyle);

function AddressData({ addressLens, countries, counties, validation }) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const classes = useStyles();
  const { countryCode } = getTenant();
  const clientQuery = useClientQueryWithErrorHandling();

  useEffect(() => {
    client.watchQuery({ query: USER_DATA, fetchPolicy: "cache-only" }).subscribe({
      next: ({ data }) => {
        (addressLens.countryId |> set)(data?.userData?.countryId);
      },
    });
  }, [client, addressLens.countryId]);

  const loadCities = useCallback(async () => {
    if (!(addressLens.countyId |> get)) return emptyArray;
    const { data } = await clientQuery(CITIES_QUERY, {
      variables: { cityTypeId: cityTypes.city, parentCityId: addressLens.countyId |> get },
    });
    return data.cities;
  }, [clientQuery, addressLens.countyId]);

  const onChangeCounty = useCallback(
    (countyId) => {
      if ((addressLens?.countyId |> get) !== countyId) {
        (addressLens.countyId |> set)(countyId);
        (addressLens.cityId |> set)(null);
      }
    },
    [addressLens.countyId, addressLens.cityId]
  );

  return (
    <Grid container spacing={2} alignContent="center" direction="row" className={classes.cardAddress}>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          label={t("Client.Country") + " *"}
          noOptionsText={t("General.AutocompleteNoOptions")}
          simpleValue={true}
          isClearable={true}
          valueKey="id"
          labelKey="name"
          options={countries}
          value={(addressLens.countryId |> get) || emptyString}
          onChange={addressLens.countryId |> set}
          error={!isValid(validation.countryId)}
          helperText={getErrors(validation.countryId)}
          disabled={Boolean(addressLens.countryId |> get)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          label={t("Client.County") + " *"}
          noOptionsText={t("General.AutocompleteNoOptions")}
          simpleValue={true}
          isClearable={true}
          valueKey="id"
          labelKey="name"
          options={counties}
          onChange={onChangeCounty}
          value={(addressLens.countyId |> get) || emptyString}
          error={!isValid(validation.countyId)}
          helperText={getErrors(validation.countyId)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <Autocomplete
          label={t("Client.City") + " *"}
          noOptionsText={t("General.AutocompleteNoOptions")}
          simpleValue={true}
          isClearable={true}
          valueKey="id"
          labelKey="name"
          loadOptions={loadCities}
          onChange={addressLens.cityId |> set}
          value={(addressLens.cityId |> get) || emptyString}
          error={!isValid(validation.cityId)}
          helperText={getErrors(validation.cityId)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <CustomTextField
          required
          fullWidth
          label={t("Client.Street")}
          value={(addressLens.street |> get) || emptyString}
          onChange={addressLens.street |> set |> onTextBoxChange}
          error={!isValid(validation.street)}
          helperText={getErrors(validation.street)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <CustomTextField
          fullWidth
          label={t("Client.Number")}
          value={(addressLens.streetNumber |> get) || emptyString}
          onChange={addressLens.streetNumber |> set |> onTextBoxChange}
          error={!isValid(validation.streetNumber)}
          helperText={getErrors(validation.streetNumber)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <CustomTextField
          required={countryCode === countryCodeConstants.unitedKingdom}
          fullWidth
          label={t("Client.Zip")}
          value={(addressLens.zip |> get) || emptyString}
          onChange={addressLens.zip |> set |> onTextBoxChange}
          error={!isValid(validation.zip)}
          helperText={getErrors(validation.zip)}
        />
      </Grid>
    </Grid>
  );
}

AddressData.propTypes = {
  addressLens: PropTypes.object.isRequired,
  countries: PropTypes.array.isRequired,
  counties: PropTypes.array.isRequired,
  validation: PropTypes.object.isRequired,
};

export default AddressData;
